/**
 * Dosetronic Charts - Daily volume visualization
 */
import { useMemo } from 'react';
import { Paper, Text, Stack, Center } from '@mantine/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { DosingEvent } from '@/types/dosing';
import { getChemicalColor } from '@/utils/chartColors';

interface DosetronicChartsProps {
  events: DosingEvent[];
}

export function DosetronicCharts({ events }: DosetronicChartsProps) {
  // Filter out excluded events before charting
  const includedEvents = useMemo(
    () => events.filter((e) => !e.excluded),
    [events],
  );

  // Process events into daily totals by chemical (server-resolved names)
  const chartData = useMemo(() => {
    const dailyData: Record<
      string,
      { date: string; [chemical: string]: number | string }
    > = {};

    includedEvents.forEach((event) => {
      const eventDate = new Date(event.timestamp);
      const dateKey = eventDate.toISOString().split('T')[0]!;
      const chemical = event.chemical_name || 'Unknown';
      const volume = event.volume_ml || 0;

      if (!dailyData[dateKey]) {
        dailyData[dateKey] = { date: dateKey };
      }

      const dayData = dailyData[dateKey]!;

      if (typeof dayData[chemical] === 'number') {
        dayData[chemical] = (dayData[chemical] as number) + volume;
      } else {
        dayData[chemical] = volume;
      }
    });

    return Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));
  }, [includedEvents]);

  // Get unique chemicals for bars
  const chemicals = useMemo(() => {
    const chemSet = new Set<string>();
    includedEvents.forEach((event) => {
      if (event.chemical_name) {
        chemSet.add(event.chemical_name);
      }
    });
    return Array.from(chemSet).sort();
  }, [includedEvents]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (chartData.length === 0) {
    return (
      <Paper p="md" withBorder>
        <Center py="xl">
          <Text c="dimmed">No dosing events to display</Text>
        </Center>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Text size="lg" fw={600}>
          Daily Volume by Chemical
        </Text>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis label={{ value: 'Volume (mL)', angle: -90, position: 'insideLeft' }} />
            <Tooltip
              labelFormatter={formatDate}
              formatter={(value: number) => `${value.toFixed(1)} mL`}
            />
            <Legend />
            {chemicals.map((chemical, index) => (
              <Bar
                key={chemical}
                dataKey={chemical}
                stackId="a"
                fill={getChemicalColor(chemical, index)}
                name={chemical}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Stack>
    </Paper>
  );
}
